import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { AiOutlineDownload, AiOutlineShareAlt } from 'react-icons/ai';
import { IoChevronBack } from 'react-icons/io5';
import Header from '../../components/Header/Header';
import StatCard from '../../components/StatCard/StatCard';
import SimCarrierDistribution from '../../components/SimCarrierDistribution/SimCarrierDistribution';
import MapDevicesPanel from '../../components/MapDevicesPanel/MapDevicesPanel';
import DataTable from '../../components/DataTable/DataTable';
import BarChartCard from '../../components/BarChartCard/BarChartCard';
import { useSchoolData } from '../../hooks/useSchoolData';
import { unslugify } from '../../data/districtData';
import { timeAgo } from '../../api/api';
import { StatCardSkeleton, SectionSkeleton, BarChartSkeleton } from '../../components/Skeleton/Skeleton';
import styles from './School.module.css';

const makeDeviceColumns = (navigate, districtSlug, circuitSlug, schoolSlug) => [
  {
    key: 'imeiNumber',
    label: 'IMEI',
    render: (v) => (
      <span
        onClick={() => navigate(`/district/${districtSlug}/circuit/${circuitSlug}/school/${schoolSlug}/device/${v}`)}
        style={{ color: 'var(--color-primary)', fontFamily: 'monospace', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      >
        {v}
      </span>
    ),
  },
  { key: 'name',         label: 'Name' },
  { key: 'manufacturer', label: 'Device' },
  { key: 'model',        label: 'Model' },
  { key: 'carrier',      label: 'Network provider' },
  { key: 'lastReportedTime', label: 'Last seen', render: timeAgo },
];

function School() {
  const { districtSlug, circuitSlug, schoolSlug } = useParams();
  const navigate = useNavigate();

  const districtName = unslugify(districtSlug);
  const circuitName  = unslugify(circuitSlug);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { loading, devicesLoading, stats = {}, carriers = [], devices = [], totalPages = 1, byGrade = [], schoolDisplayName, refresh } = useSchoolData(schoolSlug, page, search);
  const schoolName = schoolDisplayName ?? schoolSlug;

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  const breadcrumbs = [
    { label: 'Overview',   path: '/' },
    { label: districtName, path: `/district/${districtSlug}` },
    { label: circuitName,  path: `/district/${districtSlug}/circuit/${circuitSlug}` },
    { label: 'School' },
  ];

  return (
    <>
      <Header breadcrumbs={breadcrumbs} />

      <div className={styles.page}>

        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
              <IoChevronBack size={16} />
            </button>
            <h1 className={styles.pageTitle}>{schoolName}</h1>
          </div>
          <div className={styles.titleActions}>
            <RefreshButton onClick={refresh} loading={loading} />
          </div>
        </div>

        <div className={styles.metaRow}>
          <div className={styles.metaItem}>
            <div className={styles.metaLabel}>District</div>
            <div className={styles.metaValue}>{districtName}</div>
          </div>
          <div className={styles.metaDivider} />
          <div className={styles.metaItem}>
            <div className={styles.metaLabel}>Circuit</div>
            <div className={styles.metaValue}>{circuitName}</div>
          </div>
          <div className={styles.metaDivider} />
          <div className={styles.metaItem}>
            <div className={styles.metaLabel}>EMIS</div>
            <div className={styles.metaValue}>{schoolSlug}</div>
          </div>
        </div>

        <div className={styles.topRow}>
          {loading ? <StatCardSkeleton /> : <StatCard label="Devices Deployed" value={stats.devices ?? '—'} />}
          <div className={styles.simCardWrapper}>
            {loading ? <SectionSkeleton rows={3} /> : <SimCarrierDistribution carriers={carriers} title="SIM Carrier Distribution" />}
          </div>
        </div>

        <MapDevicesPanel school={schoolSlug} />

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Device Details</div>
          <DataTable
            columns={makeDeviceColumns(navigate, districtSlug, circuitSlug, schoolSlug)}
            rows={devices}
            searchable
            searchPlaceholder="Search device details"
            paginated
            pageSize={10}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onSearchChange={handleSearch}
            loadingRows={devicesLoading}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Devices by Grade</div>
          {loading
            ? <BarChartSkeleton height={320} />
            : byGrade.length > 0
              ? <BarChartCard data={byGrade} xKey="name" yKey="count" height={320} />
              : null}
        </div>

      </div>
    </>
  );
}

export default School;
